/**
 * GroupDetailPage Component
 * Displays detailed information about a group including members and job title distribution
 */

import { useState, useEffect, useMemo, useRef } from 'react';
import { hasAdminAccess } from '../lib/roles';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query';
import { X } from 'lucide-react';
import { Layout } from '../components/layout/Layout';
import { GroupFormModal } from '../components/groups/GroupFormModal';
import { Button } from '../components/ui/Button';
import { SearchableSelect } from '../components/ui/SearchableSelect';
import { LoadingSpinner } from '../components/ui/LoadingSpinner';
import { Breadcrumbs, getRootBreadcrumb, type BreadcrumbItem } from '../components/ui/Breadcrumbs';
import { groupsService } from '../services/groups.service';
import { membersService } from '../services/members.service';
import { useAuth } from '../hooks/useAuth';
import { useToast } from '../hooks/useToast';
import { getInitials } from '../lib/utils';
import { csvCell } from '../lib/csv';

export const GroupDetailPage = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const toast = useToast();
  const queryClient = useQueryClient();
  const searchableSelectRef = useRef<HTMLDivElement>(null);

  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [groupLogoUrl, setGroupLogoUrl] = useState<string | null>(null);
  const [memberImageUrls, setMemberImageUrls] = useState<Map<number, string>>(new Map());

  const groupId = Number(id);

  // Scroll to top on navigation
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [id]);

  // Fetch group details (includes members)
  const { data: group, isLoading: isLoadingGroup, dataUpdatedAt } = useQuery({
    queryKey: ['group', groupId],
    queryFn: () => groupsService.getById(groupId),
    enabled: !!groupId,
  });

  // Fetch all members (for add member picker)
  const { data: allMembers = [] } = useQuery({
    queryKey: ['members'],
    queryFn: membersService.getAll,
  });

  // Build breadcrumb hierarchy
  const breadcrumbItems = useMemo(() => {
    if (!group) return [];
    const items: BreadcrumbItem[] = [getRootBreadcrumb()];
    items.push({ label: 'Groups', to: '/groups' });
    return items;
  }, [group]);

  // Sort group members by last_name ASC
  const groupMembers = useMemo(() => {
    if (!group?.members) return [];
    return [...group.members].sort((a, b) =>
      a.last_name.localeCompare(b.last_name)
    );
  }, [group?.members]);

  // Calculate position distribution
  const positionDistribution = useMemo(() => {
    const distribution = new Map<string, number>();
    groupMembers.forEach((member) => {
      const position = member.position || 'No job title';
      distribution.set(position, (distribution.get(position) || 0) + 1);
    });
    return Array.from(distribution.entries())
      .map(([position, count]) => ({ position, count }))
      .sort((a, b) => b.count - a.count);
  }, [groupMembers]);

  // Filter available members for add member (exclude already-in-group and removed)
  const currentMemberIds = groupMembers.map((m) => m.member_id);
  const availableMembers = allMembers.filter(
    (m) => !m.removed_at && !currentMemberIds.includes(m.id)
  );

  // Load group logo
  useEffect(() => {
    if (!group?.has_group_logo) {
      setGroupLogoUrl(null);
      return;
    }

    let cancelled = false;
    setGroupLogoUrl(null);

    groupsService.fetchGroupImage(group.id).then((url) => {
      if (!cancelled && url) {
        setGroupLogoUrl(url);
      }
    });

    return () => {
      cancelled = true;
    };
  }, [group?.id, group?.has_group_logo, dataUpdatedAt]);

  // Load member profile images
  useEffect(() => {
    if (!groupMembers.length) return;
    let cancelled = false;
    const newUrls = new Map<number, string>();

    const loadImages = async () => {
      for (const member of groupMembers) {
        if (member.has_profile_picture) {
          try {
            const url = await membersService.fetchMemberImage(member.member_id);
            if (url && !cancelled) {
              newUrls.set(member.member_id, url);
            }
          } catch (error) {
            console.error(`Failed to load image for member ${member.member_id}`, error);
          }
        }
      }
      if (!cancelled) {
        setMemberImageUrls(newUrls);
      }
    };

    loadImages();
    return () => { cancelled = true; };
  }, [groupMembers]);

  // Delete group mutation
  const deleteMutation = useMutation({
    mutationFn: (id: number) => groupsService.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['groups'] });
      toast.success('Group deleted successfully');
      navigate('/groups');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error || 'Failed to delete group');
    },
  });

  // Add member mutation
  const addMemberMutation = useMutation({
    mutationFn: (memberId: number) => groupsService.addMember(groupId, memberId),
    onSuccess: () => {
      toast.success('Member added successfully');
      queryClient.invalidateQueries({ queryKey: ['group', groupId] });
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error || 'Failed to add member');
    },
  });

  // Remove member mutation
  const removeMemberMutation = useMutation({
    mutationFn: (memberId: number) => groupsService.removeMember(groupId, memberId),
    onSuccess: () => {
      toast.success('Member removed');
      queryClient.invalidateQueries({ queryKey: ['group', groupId] });
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error || 'Failed to remove member');
    },
  });

  const handleDeleteGroup = () => {
    if (group) {
      deleteMutation.mutate(group.id);
    }
  };

  const handleAddMember = (memberId: string) => {
    if (memberId) {
      addMemberMutation.mutate(Number(memberId));
    }
  };

  // CSV Export
  const handleExportCSV = () => {
    if (!group || !groupMembers.length) return;


    const csvRows: string[] = [];
    csvRows.push('First Name,Last Name,Email,Job Title');

    for (const member of groupMembers) {
      const row = [
        csvCell(member.first_name),
        csvCell(member.last_name),
        csvCell(member.email),
        csvCell(member.position || ''),
      ];
      csvRows.push(row.join(','));
    }

    const csvContent = csvRows.join('\n');

    // Create blob with UTF-8 BOM for international character support
    const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);

    const sanitizedName = group.name.replace(/[^a-zA-Z0-9-_ ]/g, '').replace(/\s+/g, '_');
    const date = new Date().toISOString().split('T')[0];
    link.href = url;
    link.download = `${sanitizedName}_members_${date}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  // Format date
  const formatDate = (dateString: string | null | undefined): string => {
    if (!dateString) return 'Unknown';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
    } catch {
      return 'Unknown';
    }
  };

  if (isLoadingGroup) {
    return (
      <Layout>
        <div className="flex justify-center items-center h-64">
          <LoadingSpinner size="lg" />
        </div>
      </Layout>
    );
  }

  if (!group) {
    return (
      <Layout>
        <div className="text-center py-12">
          <h2 className="text-2xl font-bold text-heading mb-2">Group Not Found</h2>
          <p className="text-meta mb-4">The group you're looking for doesn't exist.</p>
          <Button onClick={() => navigate('/groups')}>Back to Groups</Button>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6">
        {/* Breadcrumb Navigation */}
        <div className="flex items-center justify-between">
          <Breadcrumbs items={breadcrumbItems} />
          <Button variant="secondary" className="hidden lg:inline-flex" onClick={() => navigate('/groups')}>
            <svg
              className="h-5 w-5 mr-2"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M10 19l-7-7m0 0l7-7m-7 7h18"
              />
            </svg>
            Back to Groups
          </Button>
        </div>

        {/* Group Header */}
        <div className="bg-surface rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            {/* Left: Group Info */}
            <div className="flex items-center space-x-4">
              {/* Group Logo */}
              <div className="flex-shrink-0">
                {groupLogoUrl ? (
                  <img
                    src={groupLogoUrl}
                    alt={group.name}
                    className="w-20 h-20 rounded-full object-cover"
                  />
                ) : (
                  <div className="w-20 h-20 rounded-full bg-gradient-to-br from-gray-300 to-gray-400 dark:from-gray-600 dark:to-gray-700 flex items-center justify-center text-white text-2xl font-bold">
                    {getInitials(group.name.split(' ')[0], group.name.split(' ')[1] || '')}
                  </div>
                )}
              </div>

              {/* Group Details */}
              <div>
                <h1 className="text-3xl font-bold text-heading">{group.name}</h1>
                <div className="flex items-center space-x-4 mt-2 text-sm text-meta">
                  <span className="flex items-center">
                    <svg className="w-4 h-4 mr-1 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                    </svg>
                    {groupMembers.length} member{groupMembers.length !== 1 ? 's' : ''}
                  </span>
                  <span className="hidden lg:flex items-center">
                    <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z" clipRule="evenodd" />
                    </svg>
                    Created {formatDate(group.created_at)}
                  </span>
                  {group.updated_at && group.updated_at !== group.created_at && (
                    <span className="hidden lg:flex items-center">
                      <svg className="w-4 h-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                      </svg>
                      Updated {formatDate(group.updated_at)}
                    </span>
                  )}
                </div>
              </div>
            </div>

            {/* Right: Action Buttons (Admin Only) */}
            {hasAdminAccess(user?.role) && (
              <div className="flex items-center space-x-2">
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => setIsEditModalOpen(true)}
                >
                  <svg className="w-4 h-4 mr-1.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                  </svg>
                  Edit
                </Button>
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={handleExportCSV}
                  disabled={groupMembers.length === 0}
                >
                  <svg className="w-4 h-4 mr-1.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  Export CSV
                </Button>
                <Button
                  variant="danger"
                  size="sm"
                  onClick={() => setIsDeleteModalOpen(true)}
                >
                  <svg className="w-4 h-4 mr-1.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                  Delete
                </Button>
              </div>
            )}
          </div>
        </div>

        {/* Main Content: Members + Job Title Distribution */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Member List */}
          <div className="lg:col-span-2">
            <div className="bg-surface rounded-lg shadow">
              <div className="p-6 border-b border-[var(--border-default)]">
                <h2 className="text-xl font-semibold text-heading flex items-center">
                  <svg className="w-5 h-5 mr-2 text-blue-600 dark:text-blue-400" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M9 6a3 3 0 11-6 0 3 3 0 016 0zM17 6a3 3 0 11-6 0 3 3 0 016 0zM12.93 17c.046-.327.07-.66.07-1a6.97 6.97 0 00-1.5-4.33A5 5 0 0119 16v1h-6.07zM6 11a5 5 0 015 5v1H1v-1a5 5 0 015-5z" />
                  </svg>
                  Members ({groupMembers.length})
                </h2>
              </div>

              {groupMembers.length === 0 ? (
                <div className="p-8 text-center">
                  <svg className="mx-auto h-12 w-12 text-meta mb-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                  </svg>
                  <p className="text-sm text-meta">This group has no members yet</p>
                  {hasAdminAccess(user?.role) && (
                    <Button
                      className="mt-3"
                      onClick={() => {
                        searchableSelectRef.current?.querySelector('input')?.focus();
                      }}
                    >
                      <svg className="h-4 w-4 mr-1.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                      </svg>
                      Add First Member
                    </Button>
                  )}
                </div>
              ) : (
                <div className="divide-y divide-[var(--border-default)]">
                  {groupMembers.map((member) => (
                    <div
                      key={member.member_id}
                      className="p-4 hover:bg-surface-hover transition-colors"
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-4 min-w-0">
                          {/* Profile Picture */}
                          <div className="flex-shrink-0">
                            {memberImageUrls.get(member.member_id) ? (
                              <img
                                src={memberImageUrls.get(member.member_id)}
                                alt={`${member.first_name} ${member.last_name}`}
                                className="w-12 h-12 rounded-full object-cover"
                              />
                            ) : (
                              <div className="w-12 h-12 rounded-full bg-gradient-to-br from-gray-300 to-gray-400 dark:from-gray-600 dark:to-gray-700 flex items-center justify-center text-white text-sm font-medium">
                                {getInitials(member.first_name, member.last_name)}
                              </div>
                            )}
                          </div>

                          {/* Member Info */}
                          <div className="min-w-0">
                            <Link
                              to={`/members/${member.member_id}`}
                              className="text-base font-medium text-heading hover:text-blue-600 dark:hover:text-blue-400 transition-colors truncate block"
                            >
                              {member.first_name} {member.last_name}
                            </Link>
                            <p className="text-sm text-meta truncate">{member.position || 'No job title'}</p>
                          </div>
                        </div>

                        {/* Remove Button (Admin Only) */}
                        {hasAdminAccess(user?.role) && (
                          <button
                            onClick={() => removeMemberMutation.mutate(member.member_id)}
                            className="flex-shrink-0 ml-2 p-1 text-meta hover:text-red-500 transition-colors"
                            title="Remove member from group"
                            disabled={removeMemberMutation.isPending}
                          >
                            <X className="h-4 w-4" />
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Add Member (Admin Only) */}
              {hasAdminAccess(user?.role) && (
                <div className="p-4 border-t border-[var(--border-default)]" ref={searchableSelectRef}>
                  <SearchableSelect
                    value=""
                    onChange={handleAddMember}
                    options={[
                      { value: '', label: 'Add a member...' },
                      ...availableMembers.map((m) => ({
                        value: String(m.id),
                        label: `${m.first_name} ${m.last_name}`,
                      })),
                    ]}
                    placeholder="Search members..."
                  />
                </div>
              )}
            </div>
          </div>

          {/* Job Title Distribution + Description */}
          <div className="lg:col-span-1">
            <div className="bg-surface rounded-lg shadow p-6">
              <h2 className="text-xl font-semibold text-heading mb-4 flex items-center">
                <svg className="w-5 h-5 mr-2 text-purple-600" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M2 11a1 1 0 011-1h2a1 1 0 011 1v5a1 1 0 01-1 1H3a1 1 0 01-1-1v-5zM8 7a1 1 0 011-1h2a1 1 0 011 1v9a1 1 0 01-1 1H9a1 1 0 01-1-1V7zM14 4a1 1 0 011-1h2a1 1 0 011 1v12a1 1 0 01-1 1h-2a1 1 0 01-1-1V4z" />
                </svg>
                Job Title Distribution
              </h2>

              {positionDistribution.length === 0 ? (
                <p className="text-sm text-meta">No data</p>
              ) : (
                <div className="space-y-3">
                  {positionDistribution.map(({ position, count }) => (
                    <div key={position} className="flex items-center justify-between">
                      <span className="text-sm text-body">{position}</span>
                      <span className="text-sm font-semibold text-heading">{count}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {group.description && (
              <div className="bg-surface rounded-lg shadow p-6 mt-6">
                <h2 className="text-xl font-semibold text-heading mb-4">Description</h2>
                <p className="text-sm text-body whitespace-pre-wrap">{group.description}</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Edit Group Modal */}
      <GroupFormModal
        isOpen={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        mode="edit"
        groupId={group.id}
      />

      {/* Delete Confirmation Modal */}
      {isDeleteModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-surface-elevated rounded-lg shadow-xl max-w-md w-full p-6">
            <h3 className="text-lg font-semibold text-heading mb-2">Delete Group</h3>
            <p className="text-sm text-meta mb-6">
              Are you sure you want to delete "{group.name}"? This action cannot be undone.
            </p>
            <div className="flex justify-end space-x-3">
              <Button
                variant="secondary"
                onClick={() => setIsDeleteModalOpen(false)}
              >
                Cancel
              </Button>
              <Button
                variant="danger"
                onClick={handleDeleteGroup}
                isLoading={deleteMutation.isPending}
              >
                Delete
              </Button>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
};
